import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  usePage,
  useTokens,
} from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { relativeLuminance } from '../utils/luminance';
import { handleScheduleTourClick, scheduleTourHref } from '../utils/openScheduleTour';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation.
    Any page that declares a `parent` in pages.json is ALSO excluded — nested
    pages (apply-now, resident-access, faq, pet-policy, etc.) do
    not surface in the header nav. They remain reachable via direct URLs and
    the footer/sitemap. */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'thank-you',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

// ============================================================================
// Office-hours helpers (used by the mobile/tablet drawer's HOURS section).
// Kept local to this file rather than shared with Footer's copy — the two
// components have divergent styling wrappers and lifting these ~30 lines
// into a shared util isn't a large enough win to justify a new file.
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  label: string;
  hours: string;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

function hasHoursValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const n = val.trim().toLowerCase();
  return n !== '' && n !== 'false' && n !== 'null' && n !== 'undefined' && n !== '0' && n !== 'closed';
}

function formatHoursTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const entriesByDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasHoursValue(entry.day)) continue;
    const key = entry.day!.trim().toLowerCase().slice(0, 3);
    if (DAY_ORDER.includes(key) && !entriesByDay.has(key)) {
      entriesByDay.set(key, entry);
    }
  }
  const groups: GroupedHours[] = [];
  for (const key of DAY_ORDER) {
    const entry = entriesByDay.get(key);
    const dayLabel = DAY_LABELS[key];
    const isClosed = !entry || !hasHoursValue(entry.open_time) || !hasHoursValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatHoursTime(entry.open_time || '')} – ${formatHoursTime(entry.close_time || '')}`;
    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' – ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} – ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }
  return groups;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible, not in the exclusion list, and not
 * declared as nested (`parent: <alias>`) — nested pages are surfaced elsewhere
 * (footer, sitemap, direct URL) rather than in the header nav. Sorted by
 * position, flat single-level list — no dropdowns.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  return Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      // Skip pages nested under a parent — they're intentionally hidden from
      // the top-level nav even if they'd otherwise pass every other check.
      if (page.parent) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([, page]) => ({ label: page.name, href: page.path }));
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'SCHEDULE A TOUR' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: {
      label: 'Phone Text',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentPortalLinkDef: SlotGroupDefinition = {
  label: 'Resident Portal Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/resident-access',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Portal' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/apply-now',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Helpers
// ============================================================================

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function digitsOnly(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/[^+\d]/g, '');
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = digitsOnly(raw);
  return digits ? `tel:${digits}` : raw;
}

/**
 * Strip a trailing country segment ("USA", "U.S.A.", "United States",
 * "United States of America") from the property address. Runs against the
 * final comma-separated segment so we never chew into a city/state that
 * happens to contain "US" as a substring.
 */
function stripCountry(address: string | null | undefined): string {
  if (!address) return '';
  const trimmed = address.trim();
  const lastComma = trimmed.lastIndexOf(',');
  if (lastComma === -1) return trimmed;
  const tail = trimmed.slice(lastComma + 1).trim().toLowerCase().replace(/\./g, '');
  if (
    tail === 'usa' ||
    tail === 'us' ||
    tail === 'united states' ||
    tail === 'united states of america'
  ) {
    return trimmed.slice(0, lastComma).trim();
  }
  return trimmed;
}

/**
 * Split a full address into street (line 1) and city/state/ZIP (line 2)
 * at the first comma. Used for the drawer "Visit Us" address on mobile.
 */
function splitAddressLines(address: string): { street: string; locality: string | null } {
  const idx = address.indexOf(',');
  if (idx === -1) return { street: address, locality: null };
  const street = address.slice(0, idx).trim();
  const locality = address.slice(idx + 1).trim();
  return { street: street || address, locality: locality || null };
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link (with optional hover/focus dropdown)
// ============================================================================

function DesktopNavLink({ link, isActive }: { link: NavLink; isActive: (href: string) => boolean }) {
  const hasChildren = !!link.children?.length;
  return (
    <div className={`sh-navitem${hasChildren ? ' sh-navitem--has-children' : ''}`}>
      <a
        href={link.href}
        className={`sh-navlink${isActive(link.href) ? ' sh-navlink--active' : ''}`}
      >
        {link.label}
        {hasChildren ? <ChevronDownIcon className="sh-navlink-caret" /> : null}
      </a>
      {hasChildren ? (
        <div className="sh-dropdown" role="menu">
          {link.children!.map((child) => (
            <a
              key={child.href}
              href={child.href}
              role="menuitem"
              className={`sh-dropdown-link${isActive(child.href) ? ' sh-dropdown-link--active' : ''}`}
            >
              {child.label}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================
// Renders a single row of the off-canvas drawer's nav. Parents with children
// render as [<link> <chevron toggle>]; the chevron toggles a collapsible
// children container in the caller. Children render as indented plain links
// (no chevron). This mirrors the desktop dropdown's "hover to reveal" pattern
// with a touch-friendly click-to-expand instead of pure :hover, which touch
// screens don't fire reliably.
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isActive,
  isChild,
  isOpen,
  onToggleChildren,
}: {
  link: NavLink;
  onNavigate: () => void;
  isActive?: boolean;
  isChild?: boolean;
  isOpen?: boolean;
  onToggleChildren?: () => void;
}) {
  const hasChildren = !!link.children?.length;
  const rowClasses = [
    'menu-nav-row',
    hasChildren ? 'menu-nav-row--has-children' : '',
    isChild ? 'menu-nav-row--child' : '',
  ].filter(Boolean).join(' ');
  const linkClasses = [
    'menu-nav-link',
    'block',
    'py-2.5',
    'md:py-3',
    'font-body',
    'text-base',
    'md:text-lg',
    'font-medium',
    'tracking-wide',
    'text-white',
    'transition-colors',
    isActive ? 'menu-nav-link--active' : '',
    isChild ? 'menu-nav-link--child' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={rowClasses}>
      <a href={link.href} onClick={onNavigate} className={linkClasses}>
        <span className="inline-flex items-center gap-2">{link.label}</span>
      </a>
      {hasChildren ? (
        <button
          type="button"
          onClick={onToggleChildren}
          className={`menu-nav-toggle${isOpen ? ' menu-nav-toggle--open' : ''}`}
          aria-expanded={!!isOpen}
          aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${link.label} submenu`}
        >
          <ChevronDownIcon className="menu-nav-toggle-icon" />
        </button>
      ) : null}
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: A floating, primary-colored "pill" navigation bar.
//
// Aion Management override of the Stadium House header — the parent theme
// centers the logo and splits nav links to either side. Aion's brand
// direction on desktop is a Western-style layout: logo pinned to the LEFT
// edge, all nav links right-aligned in a single group, with the
// "SCHEDULE A TOUR" CTA pill sitting as the last item on the right. On
// mobile, tablet, and smaller desktops the hamburger sits on the left
// with the logo centered.
//
// - Mobile (<md): hamburger on the LEFT, logo CENTERED. Nav lives in the
//   full-height drawer.
// - Tablet and smaller desktop (md — <xl / <1280px): hamburger + phone on
//   the LEFT, logo CENTERED, "SCHEDULE A TOUR" CTA on the RIGHT. Nav links
//   stay hidden and live in the drawer. The utility top bar is hidden.
// - Desktop (xl / 1280px+): two-part layout — logo left, all nav links + CTA
//   right. No hamburger.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Tracks which parent nav items are expanded in the drawer. Keyed by href so
  // the state survives navLinks recomputation without stale entries pinning
  // groups open. Reset whenever the drawer closes so it opens fresh next time.
  const [openParents, setOpenParents] = useState<Set<string>>(new Set());

  // Scroll detection — adds a subtle elevation/shrink once the page scrolls.
  useEffect(() => {
    let ticking = false;
    const SCROLL_AT = 20;
    const UNSCROLL_AT = 8;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(prev => {
          if (!prev && y > SCROLL_AT) return true;
          if (prev && y < UNSCROLL_AT) return false;
          return prev;
        });
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const pageCtx = usePage();

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive leasing
  // view" widget on that page. We require BOTH the JavaScript ID and the
  // Config ID; a property with only one populated is a half-configured state.
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Aion layout: all nav items sit together on the right side of the pill.
  // We keep the "Home" entry hidden from the top nav for parity with the
  // parent theme's list — sites that want a home link surface it via the
  // logo (which links to /) — but this could easily flip to
  // `[{ label: 'Home', href: '/' }, ...navLinks]` if requested.
  const rightLinks = useMemo<NavLink[]>(() => navLinks, [navLinks]);

  const currentPath = pageCtx?.path
    || (typeof window !== 'undefined' ? window.location.pathname : '/');

  const isNavActive = useCallback((href: string) => {
    const normalized = currentPath.endsWith('/') && currentPath !== '/'
      ? currentPath.slice(0, -1)
      : currentPath;
    const target = href.endsWith('/') && href !== '/'
      ? href.slice(0, -1)
      : href;
    if (target === '/') return normalized === '/';
    return normalized === target || normalized.startsWith(`${target}/`);
  }, [currentPath]);

  const propertyAddressUrl = usePropertyAddressUrl();
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const defaultTelUrl = toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);

  // Office hours — drives the "HOURS" block at the bottom of the drawer.
  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = useMemo(
    () => (officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : []),
    [officeHours],
  );

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const headerLogo = monoWhiteLogo || logoRaw.image;

  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const phoneLink = useSlotGroup('header.phone_link', phoneLinkDef);
  const residentPortalLink = useSlotGroup('header.resident_portal_link', residentPortalLinkDef);
  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);

  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || defaultTelUrl;
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '(555) 555-5555';
  const addressDisplay = stripCountry(addressLink.text) || '123 Main Street, Atlanta, GA 30313';
  const addressLines = splitAddressLines(addressDisplay);
  // Drawer brand block reuses the same logo/alt slot. We compose the source
  // image inline (mono-white wins if the property model provides it) but read
  // `alt` straight from `logoRaw` — spreading it into a new object narrows
  // the inferred type to `{ image }` and drops the sibling fields, so we keep
  // the fields as two separate locals instead.
  const menuLogoImage = monoWhiteLogo || logoRaw.image;
  const menuLogoAlt = logoRaw.alt || 'Community Logo';

  // Top bar contrast: black or white text against the secondary surface so
  // the utility strip stays readable across light and dark brand palettes.
  const tokens = useTokens();
  const topBarFg = useMemo(() => {
    const secondary = tokens?.color?.secondary;
    if (!secondary) return '#ffffff';
    return relativeLuminance(secondary) > 0.55 ? '#000000' : '#ffffff';
  }, [tokens]);

  // Publish --top-bar-height so heroes can clear the fixed utility strip.
  // Hidden breakpoints (tablet and smaller desktop) collapse to 0 via display:none.
  const topBarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = topBarRef.current;
    if (!el) return;
    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--top-bar-height', `${h}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener('resize', publish);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', publish);
      document.documentElement.style.setProperty('--top-bar-height', '0px');
    };
  }, []);

  useSiteCtaStyles();

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="montserrat"]')) return;
    if (!document.querySelector('link[data-vibe-font-preconnect="google"]')) {
      const preconnect1 = document.createElement('link');
      preconnect1.rel = 'preconnect';
      preconnect1.href = 'https://fonts.googleapis.com';
      preconnect1.setAttribute('data-vibe-font-preconnect', 'google');
      document.head.appendChild(preconnect1);
      const preconnect2 = document.createElement('link');
      preconnect2.rel = 'preconnect';
      preconnect2.href = 'https://fonts.gstatic.com';
      preconnect2.crossOrigin = 'anonymous';
      preconnect2.setAttribute('data-vibe-font-preconnect', 'gstatic');
      document.head.appendChild(preconnect2);
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'montserrat');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when mobile menu is open, and mark the document so
  // third-party overlays (EliseAI chat) can be tucked below the drawer.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    document.body.classList.toggle('header-menu-open', menuOpen);
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('header-menu-open');
    };
  }, [menuOpen]);

  // When the drawer opens, pre-expand the parent group that contains the
  // currently active page so the active child is visible without an extra tap.
  // When it closes, collapse everything so the next open reads as a fresh state.
  useEffect(() => {
    if (!menuOpen) {
      setOpenParents(new Set());
      return;
    }
    const activeParents = new Set<string>();
    for (const link of navLinks) {
      if (link.children?.some((child) => isNavActive(child.href))) {
        activeParents.add(link.href);
      }
    }
    if (activeParents.size > 0) setOpenParents(activeParents);
  }, [menuOpen, navLinks, isNavActive]);

  const closeMobileMenu = () => setMenuOpen(false);

  const toggleParentOpen = useCallback((href: string) => {
    setOpenParents((prev) => {
      const next = new Set(prev);
      if (next.has(href)) next.delete(href);
      else next.add(href);
      return next;
    });
  }, []);

  const LogoLink = (
    <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef} className="sh-logo-slot">
      <a href="/" className="sh-logo" aria-label="Home">
        {headerLogo ? (
          <VibesitesImage
            url={headerLogo}
            alt={logoRaw.alt || 'Community Logo'}
            sizes="(max-width: 1279px) 192px, 256px"
            className="sh-logo-img"
          />
        ) : (
          <span className="sh-logo-fallback">{pageCtx?.alias ? 'Home' : 'Stadium House'}</span>
        )}
      </a>
    </EditableSlotGroup>
  );

  return (
    <>
      <header
        className={`sh-header fixed left-0 right-0 w-full ${menuOpen ? 'z-[100000]' : 'z-50'}${scrolled ? ' sh-header--scrolled' : ''}`}
        style={{ top: 0 }}
      >
        {/* Offer banner clears via padding; top bar sits full-bleed above the
            floating pill nav without changing the navbar itself. */}
        <div style={{ paddingTop: 'var(--offer-banner-height, 0px)' }}>
          <div
            ref={topBarRef}
            className="sh-topbar"
            style={{
              background: 'var(--vibe-color-secondary)',
              color: topBarFg,
              ['--sh-topbar-fg' as string]: topBarFg,
            }}
          >
            <div className="sh-topbar-inner">
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressHref}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="sh-topbar-address"
                >
                  {addressDisplay}
                </a>
              </EditableSlotGroup>

              <div className="sh-topbar-actions">
                <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                  <a
                    href={phoneHref || '#'}
                    target={phoneLink._target || '_self'}
                    className="sh-topbar-phone dniphone dniphonehref"
                  >
                    {phoneDisplay}
                  </a>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="header.resident_portal_link" definition={residentPortalLinkDef}>
                  <a
                    href={residentPortalLink.url || '/resident-access'}
                    target={residentPortalLink._target || '_self'}
                    rel={residentPortalLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                    className="sh-topbar-link"
                  >
                    {residentPortalLink.label || 'Resident Portal'}
                  </a>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    rel={applyNowLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                    className="sh-topbar-link"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>

          <div className="sh-header-inner">
            <div className="sh-navbar">
              {/* Leading cluster — hamburger (mobile, tablet, and smaller desktop)
                  and the phone beside it from tablet up. Sits on the left of
                  the centered logo. Hidden entirely on wide desktop. */}
              <div className="sh-nav-leading">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="sh-hamburger"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span className={`sh-hamburger-bar ${menuOpen ? 'rotate-45 translate-y-[0px]' : '-translate-y-[6px]'}`} />
                  <span className={`sh-hamburger-bar ${menuOpen ? 'opacity-0' : ''}`} />
                  <span className={`sh-hamburger-bar ${menuOpen ? '-rotate-45 translate-y-[0px]' : 'translate-y-[6px]'}`} />
                </button>
                <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                  <a
                    href={phoneHref || '#'}
                    target={phoneLink._target || '_self'}
                    className="sh-tablet-phone dniphone dniphonehref"
                  >
                    {phoneDisplay}
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Logo — centered below 1280px, left on wide desktop */}
              {LogoLink}

              {/* Right nav (wide-desktop links + CTA; tablet and smaller desktop show CTA only) */}
              <nav className="sh-nav-side sh-nav-right" aria-label="Primary">
                {rightLinks.map((link) => (
                  <DesktopNavLink key={link.href} link={link} isActive={isNavActive} />
                ))}
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourHref(scheduleTourLink.url)}
                    target={scheduleTourLink._target || '_self'}
                    rel={scheduleTourLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                    className="header-schedule-cta"
                    data-vibesite-event="schedule_tour_clicked"
                    onClickCapture={(e) =>
                      handleScheduleTourClick(e, scheduleTourLink.url || '/schedule-a-tour')
                    }
                  >
                    {scheduleTourLink.label || 'SCHEDULE A TOUR'}
                  </a>
                </EditableSlotGroup>
              </nav>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Off-canvas drawer — slides in from the LEFT below 1280px.     */}
        {/* Solid primary background with logo, nav, and Visit / Contact  */}
        {/* / Hours blocks stacked in a single column. Wide desktop       */}
        {/* (xl+) never sees this — the hamburger is hidden.              */}
        {/* ============================================================== */}
        <div
          aria-hidden={!menuOpen}
          className={`menu-backdrop fixed inset-0 z-[100000] bg-black/40 transition-opacity duration-300 xl:hidden ${
            menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeMobileMenu}
        />

        <aside
          aria-hidden={!menuOpen}
          aria-label="Site menu"
          className={`mobile-menu-panel fixed left-0 top-0 bottom-0 z-[100001] flex flex-col overflow-hidden transition-transform duration-300 ease-out xl:hidden ${
            menuOpen ? 'translate-x-0 mobile-menu-open' : '-translate-x-full pointer-events-none'
          }`}
        >
          {/* Close button pinned to the TOP-RIGHT of the panel, near the
              open edge facing the page. Kept outside the scroll
              container so it stays anchored regardless of the drawer's
              inner scroll position. */}
          <button
            type="button"
            onClick={closeMobileMenu}
            className="menu-close-btn"
            aria-label="Close menu"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>

          <div className="menu-scroll flex-1 overflow-y-auto">
            {/* Brand block — big centered logo */}
            <div className="menu-brand">
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" onClick={closeMobileMenu} className="menu-brand-logo-link" aria-label="Home">
                  {menuLogoImage ? (
                    <VibesitesImage
                      url={menuLogoImage}
                      alt={menuLogoAlt}
                      sizes="180px"
                      className="menu-brand-logo"
                    />
                  ) : (
                    <span className="menu-brand-fallback">Stadium House</span>
                  )}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Primary navigation. Parent items with children collapse their
                children into a submenu that opens on tap of the chevron (and
                on hover via CSS on pointer devices), mirroring the desktop
                nav's hover-to-reveal dropdown. */}
            <nav role="navigation" aria-label="Main navigation" className="menu-nav">
              <MobileNavLink
                link={{ label: 'Home', href: '/' }}
                onNavigate={closeMobileMenu}
                isActive={isNavActive('/')}
              />
              {navLinks.map((link) => {
                const isOpen = openParents.has(link.href);
                const hasChildren = !!link.children?.length;
                return (
                  <div
                    key={link.href}
                    className={`menu-nav-group${hasChildren ? ' menu-nav-group--has-children' : ''}${
                      isOpen ? ' menu-nav-group--open' : ''
                    }`}
                  >
                    <MobileNavLink
                      link={link}
                      onNavigate={closeMobileMenu}
                      isActive={isNavActive(link.href)}
                      isOpen={isOpen}
                      onToggleChildren={hasChildren ? () => toggleParentOpen(link.href) : undefined}
                    />
                    {hasChildren ? (
                      <div
                        className={`menu-nav-children${isOpen ? ' menu-nav-children--open' : ''}`}
                        aria-hidden={!isOpen}
                      >
                        {link.children!.map((child) => (
                          <MobileNavLink
                            key={child.href}
                            link={child}
                            onNavigate={closeMobileMenu}
                            isActive={isNavActive(child.href)}
                            isChild
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </nav>

            {/* Mobile/tablet CTAs — Schedule A Tour / Apply Now / Resident
                Portal. DOM order is after nav (tablet layout); on mobile
                (<md) flex `order` pulls this block above the nav links.
                Reuses the same slot groups as the top bar and desktop pill
                so editors keep a single source of truth for labels and URLs.
                All three drawer CTAs share the filled .menu-cta pill style
                (same recipe as Footer › cta_tour_link / .footer-cta-button). */}
            <div className="menu-ctas">
              <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                <a
                  href={scheduleTourHref(scheduleTourLink.url)}
                  target={scheduleTourLink._target || '_self'}
                  rel={scheduleTourLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="menu-cta"
                  data-vibesite-event="schedule_tour_clicked"
                  onClickCapture={(e) => {
                    closeMobileMenu();
                    handleScheduleTourClick(e, scheduleTourLink.url || '/schedule-a-tour');
                  }}
                >
                  {scheduleTourLink.label || 'SCHEDULE A TOUR'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  rel={applyNowLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                  onClick={closeMobileMenu}
                  className="menu-cta"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_portal_link" definition={residentPortalLinkDef}>
                <a
                  href={residentPortalLink.url || '/resident-access'}
                  target={residentPortalLink._target || '_self'}
                  rel={residentPortalLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                  onClick={closeMobileMenu}
                  className="menu-cta"
                >
                  {residentPortalLink.label || 'Resident Portal'}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Contact / hours info blocks — one per row, small caps
                heading over the value. Matches the reference. */}
            <div className="menu-info">
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <div className="menu-info-block">
                  <div className="menu-info-label">Visit Us</div>
                  <a
                    href={addressHref}
                    target={addressLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="menu-info-value menu-info-address"
                  >
                    <span className="menu-info-address-line">{addressLines.street}</span>
                    {addressLines.locality ? (
                      <span className="menu-info-address-line">{addressLines.locality}</span>
                    ) : null}
                  </a>
                </div>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                <div className="menu-info-block">
                  <div className="menu-info-label">Contact Us</div>
                  <a
                    href={phoneHref || '#'}
                    target={phoneLink._target || '_self'}
                    className="menu-info-value dniphone dniphonehref"
                  >
                    {phoneDisplay}
                  </a>
                </div>
              </EditableSlotGroup>

              {groupedHours.length > 0 ? (
                <div className="menu-info-block">
                  <div className="menu-info-label">Hours</div>
                  <dl className="menu-hours">
                    {groupedHours.map((group, index) => (
                      <div key={index} className="menu-hours-row">
                        <dt>{group.label}</dt>
                        <dd>{group.hours}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null}
            </div>
          </div>
        </aside>
      </header>

      <style>{`
        /* ================================================================ */
        /* Utility top bar — secondary strip above the floating pill nav   */
        /* ================================================================ */
        .sh-topbar {
          width: 100%;
          font-family: 'Montserrat', var(--vibe-font-body), sans-serif;
        }
        .sh-topbar-inner {
          display: flex;
          align-items: center;
          /* Mobile: address is the only visible item — center it. */
          justify-content: center;
          gap: 1rem;
          padding: 0.55rem 1rem;
        }
        .sh-topbar-address,
        .sh-topbar-phone,
        .sh-topbar-link {
          color: var(--sh-topbar-fg, #fff);
          text-decoration: none;
          font-size: 0.8125rem;
          line-height: 1.3;
          letter-spacing: 0.01em;
          white-space: nowrap;
        }
        .sh-topbar-address {
          font-weight: 400;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: center;
        }
        .sh-topbar-phone {
          display: inline-flex;
          align-items: center;
          font-weight: 700;
          /* Keep phone clear of the flush Resident Portal / Apply Now pair. */
          padding-right: 1.75rem;
        }
        /* Resident Portal / Apply Now — Belmont-style util CTAs: stretch
           the full top-bar height so hover fills a slightly lighter mix of
           the secondary strip (not an underline). */
        .sh-topbar-link {
          display: inline-flex;
          align-items: center;
          align-self: stretch;
          font-weight: 400;
          padding: 0 1.25rem;
          background-color: transparent;
          transition: background-color 0.2s ease, color 0.2s ease;
        }
        .sh-topbar-address:hover,
        .sh-topbar-phone:hover {
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .sh-topbar-link:hover {
          background-color: color-mix(in srgb, var(--vibe-color-secondary) 70%, white);
          color: var(--sh-topbar-fg, #fff);
          text-decoration: none;
        }
        /* Mobile: address only */
        .sh-topbar-actions {
          display: none;
          align-items: stretch;
          gap: 0;
          flex-shrink: 0;
          align-self: stretch;
        }
        /* EditableSlotGroup wrappers should not interrupt the flex row. */
        .sh-topbar-actions > * {
          display: contents;
        }
        /* Tablet and smaller desktop (<1280px): hide the entire top bar */
        @media (min-width: 768px) and (max-width: 1279.98px) {
          .sh-topbar {
            display: none;
          }
        }
        /* Wide desktop (1280px+): full utility strip — address left, actions right */
        @media (min-width: 1280px) {
          .sh-topbar-inner {
            align-items: stretch;
            /* Vertical padding lives on address/phone so CTAs can flush-fill. */
            padding: 0 0 0 1.75rem;
            justify-content: space-between;
          }
          .sh-topbar-address {
            text-align: left;
            align-self: center;
            padding: 0.6rem 0;
          }
          .sh-topbar-phone {
            align-self: center;
            padding-top: 0.6rem;
            padding-bottom: 0.6rem;
          }
          .sh-topbar-actions {
            display: flex;
          }
          .sh-topbar-link {
            padding: 0 1.75rem;
          }
        }

        /* ================================================================ */
        /* Floating pill navbar — Aion "logo left / nav right" layout      */
        /* ================================================================ */
        .sh-header-inner {
          padding-left: 1rem;
          padding-right: 1rem;
        }
        @media (min-width: 768px) {
          .sh-header-inner {
            padding-left: 1.75rem;
            padding-right: 1.75rem;
          }
        }
        .sh-navbar {
          /* Below 1280px: equal side tracks so the centered logo stays
             optically centered even when the left cluster (hamburger, and
             from tablet up also phone) and the right track have different
             widths. On mobile the right track is empty (nav is display:none)
             but the 1fr column still balances the hamburger on the left. */
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          margin-top: 0.85rem;
          /* Extra vertical padding keeps tall logos from kissing the pill edge. */
          padding: 0.7rem 0.75rem 0.7rem 0.65rem;
          border-radius: 9999px;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
          transition: padding 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
        }
        @media (min-width: 768px) {
          .sh-navbar {
            padding: 0.65rem 0.75rem 0.65rem 0.85rem;
          }
        }
        @media (min-width: 1280px) {
          .sh-navbar {
            /* Wide desktop: logo left, nav+CTA right. Leading cluster is hidden. */
            grid-template-columns: auto minmax(0, 1fr);
            padding: 0.65rem 1.25rem;
          }
        }
        .sh-header--scrolled .sh-navbar {
          box-shadow: 0 10px 34px rgba(0, 0, 0, 0.28);
        }

        /* Leading cluster — hamburger + phone (phone from tablet through smaller desktop) */
        .sh-nav-leading {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          flex-shrink: 0;
          grid-column: 1;
          grid-row: 1;
          justify-self: start;
        }
        @media (min-width: 768px) {
          .sh-nav-leading {
            gap: 0.85rem;
          }
        }
        @media (min-width: 1280px) {
          .sh-nav-leading { display: none; }
        }

        /* Phone between hamburger and centered logo — tablet and smaller desktop */
        .sh-tablet-phone {
          display: none;
        }
        @media (min-width: 768px) and (max-width: 1279.98px) {
          .sh-tablet-phone {
            display: inline-flex;
            align-items: center;
            font-family: var(--vibe-font-body);
            font-size: 0.95rem;
            font-weight: 500;
            letter-spacing: 0.01em;
            line-height: 1;
            color: var(--vibe-color-text-on-primary, #fff);
            text-decoration: none;
            white-space: nowrap;
            transition: opacity 0.2s ease;
          }
          .sh-tablet-phone:hover {
            opacity: 0.85;
          }
        }

        /* Logo — centered below 1280px, left on wide desktop.
           Fixed height + width keep property logos at a consistent size
           regardless of asset aspect ratio (object-fit: contain).
           display:contents on the EditableSlotGroup so .sh-logo is the
           real grid item and stays aligned with leading/nav tracks. */
        .sh-logo-slot {
          display: contents;
        }
        .sh-logo {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          grid-column: 2;
          grid-row: 1;
          justify-self: center;
          align-self: center;
          height: 3.75rem;
        }
        @media (min-width: 1280px) {
          .sh-logo {
            grid-column: 1;
            justify-self: start;
            justify-content: flex-start;
            height: 4.25rem;
          }
        }
        .sh-logo-img {
          display: block;
          height: 3.75rem;
          width: 12rem;
          max-height: 3.75rem;
          max-width: min(12rem, 48vw);
          object-fit: contain;
          object-position: center;
        }
        @media (min-width: 1280px) {
          .sh-logo-img {
            height: 4.25rem;
            width: 16rem;
            max-height: 4.25rem;
            max-width: 16rem;
            /* Left-align the graphic so it lines up with the pill edge /
               nav baseline instead of floating in the fixed-width box. */
            object-position: left center;
          }
        }
        .sh-logo-fallback {
          font-family: var(--vibe-font-heading);
          font-weight: 700;
          font-size: 1.15rem;
          color: var(--vibe-color-text-on-primary, #fff);
          white-space: nowrap;
        }

        /* Nav side — hidden on mobile.
           From tablet through smaller desktop (<1280px) the nav renders so
           the CTA is visible but the individual nav-item links stay in the
           drawer. On wide desktop (1280px+) the nav shows all links + CTA,
           right-aligned. */
        .sh-nav-side {
          display: none;
        }
        @media (min-width: 768px) {
          .sh-nav-right {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.35rem;
            min-width: 0;
            grid-column: 3;
            grid-row: 1;
            justify-self: end;
          }
          /* Below 1280px: hide the individual nav items so only the
             schedule CTA is visible inside the right nav. */
          .sh-nav-right .sh-navitem { display: none; }
        }
        @media (min-width: 1280px) {
          .sh-nav-side {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            min-width: 0;
          }
          /* Right-align every child of the right nav so the link cluster
             hugs the pill's right edge, with the CTA as the last item. */
          .sh-nav-right { grid-column: 2; justify-content: flex-end; }
          /* Un-hide the nav items on wide desktop — the override above
             collapsed them so only the CTA remained visible. */
          .sh-nav-right .sh-navitem { display: inline-flex; }
        }

        .sh-navitem {
          position: relative;
          display: inline-flex;
        }
        .sh-navlink {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.55rem 0.6rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          font-weight: 500;
          letter-spacing: 0.01em;
          line-height: 1;
          color: color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 88%, transparent);
          text-decoration: none;
          white-space: nowrap;
          border-radius: 9999px;
          transition: color 0.2s ease, background 0.2s ease;
        }
        @media (min-width: 1280px) {
          .sh-navlink { font-size: 0.95rem; padding: 0.55rem 0.7rem; }
        }
        .sh-navlink:hover {
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .sh-navlink--active {
          color: var(--vibe-color-text-on-primary, #fff);
          font-weight: 600;
        }
        .sh-navlink--active::after {
          content: '';
          position: absolute;
          left: 0.6rem;
          right: 0.6rem;
          bottom: 0.3rem;
          height: 2px;
          border-radius: 2px;
          background: currentColor;
        }
        .sh-navlink-caret {
          width: 0.85rem;
          height: 0.85rem;
          opacity: 0.85;
          transition: transform 0.2s ease;
        }
        .sh-navitem:hover .sh-navlink-caret,
        .sh-navitem:focus-within .sh-navlink-caret {
          transform: rotate(180deg);
        }

        /* Dropdown */
        .sh-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 0.5rem;
          min-width: 12rem;
          padding: 0;
          overflow: hidden;
          background: var(--vibe-color-primary);
          border: none;
          border-radius: 0.75rem;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.16);
          opacity: 0;
          visibility: hidden;
          transform: translateY(6px);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0s linear 0.2s;
          z-index: 10;
        }
        .sh-nav-right .sh-dropdown {
          left: auto;
          right: 0;
        }
        .sh-navitem:hover .sh-dropdown,
        .sh-navitem:focus-within .sh-dropdown {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0s linear 0s;
        }
        .sh-dropdown-link {
          display: block;
          padding: 0.75rem 1.1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--vibe-color-text-on-primary, #fff);
          text-decoration: none;
          border-radius: 0;
          white-space: nowrap;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .sh-dropdown-link + .sh-dropdown-link {
          border-top: 1px solid color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 15%, transparent);
        }
        .sh-dropdown-link:hover {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #fff);
        }
        .sh-dropdown-link--active {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #fff);
          font-weight: 600;
        }

        /* Desktop/tablet SCHEDULE A TOUR CTA — matches Footer ›
           cta_tour_link (.footer-cta-button): white pill, primary label,
           surface fill + contrast ink on hover. Drawer CTAs use the same
           recipe via .menu-cta. */
        a.header-schedule-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          padding: 0.7rem 1.5rem;
          border-radius: 999px;
          background: #fff;
          border: 1px solid #fff;
          color: var(--vibe-color-primary, #12244a);
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: 0.875rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          line-height: 1.25;
          text-align: center;
          text-decoration: none;
          white-space: nowrap;
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
        }
        @media (min-width: 1280px) {
          a.header-schedule-cta {
            padding: 0.85rem 1.75rem;
            font-size: 0.95rem;
          }
        }
        a.header-schedule-cta:hover,
        a.header-schedule-cta:focus-visible {
          background: var(--vibe-color-surface, #fff);
          border-color: var(--vibe-color-surface, #fff);
          color: var(--site-cta-ink-on-surface, #fff);
          transform: translateY(-1px);
        }
        a.header-schedule-cta:focus-visible {
          outline: 2px solid #ffffff;
          outline-offset: 3px;
        }

        /* Hamburger — visible below 1280px (LEFT side of the grid,
           inside .sh-nav-leading). Hidden on wide desktop. */
        .sh-hamburger {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.75rem;
          height: 2.75rem;
          flex-shrink: 0;
          border-radius: 9999px;
          background: transparent;
          border: none;
          cursor: pointer;
        }
        @media (min-width: 1280px) {
          .sh-hamburger { display: none; }
        }
        .sh-hamburger-bar {
          position: absolute;
          display: block;
          width: 1.4rem;
          height: 2px;
          border-radius: 2px;
          background: var(--vibe-color-text-on-primary, #fff);
          transition: transform 0.3s ease, opacity 0.3s ease;
        }

        /* ================================================================= */
        /* Off-canvas drawer — below 1280px, slides in from the LEFT        */
        /* ================================================================= */
        .mobile-menu-panel {
          /* Reveal ~3rem of the underlying page on mobile so the user has
             an obvious "tap here to close" gutter. Fixed width on tablet
             so the panel doesn't grow awkwardly wide. */
          width: calc(100% - 3rem);
          max-width: 22rem;
          background-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          padding: 1.25rem 1.5rem 2rem;
        }
        @media (min-width: 640px) {
          .mobile-menu-panel {
            max-width: 24rem;
            padding: 1.5rem 2rem 2.5rem;
          }
        }
        @media (min-width: 768px) {
          .mobile-menu-panel {
            max-width: 26rem;
          }
        }
        .mobile-menu-panel.mobile-menu-open {
          box-shadow: 16px 0 50px rgba(0, 0, 0, 0.4);
        }
        .menu-backdrop {
          cursor: pointer;
        }
        /* EliseAI (and similar) chat launchers sit at very high z-index and
           otherwise paint on top of the open hamburger drawer on mobile.
           Hide them only while the nav is open; restore when it closes. */
        body.header-menu-open .meetelise-chat,
        body.header-menu-open [class*="meetelise-chat"],
        body.header-menu-open iframe[src*="eliseai"],
        body.header-menu-open iframe[src*="meetelise"] {
          visibility: hidden !important;
          pointer-events: none !important;
        }
        .menu-scroll {
          display: flex;
          flex-direction: column;
        }
        /* Drawer stack order: mobile puts CTAs above nav; tablet (md+) keeps
           DOM order (nav, then CTAs). Close button stays outside .menu-scroll. */
        .menu-brand { order: 1; }
        .menu-ctas { order: 2; }
        .menu-nav { order: 3; }
        .menu-info { order: 4; }
        @media (min-width: 768px) {
          .menu-nav { order: 2; }
          .menu-ctas { order: 3; }
        }

        /* Close button — top RIGHT of the panel */
        .mobile-menu-panel .menu-close-btn {
          align-self: flex-end;
          width: 2.5rem;
          height: 2.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.9);
          background: transparent;
          border: none;
          cursor: pointer;
          margin: 0 -0.5rem 0.5rem 0;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .mobile-menu-panel .menu-close-btn:hover {
          color: #fff;
        }
        .mobile-menu-panel .menu-close-btn svg {
          width: 1.5rem;
          height: 1.5rem;
        }

        /* Brand block */
        .menu-brand {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 0.5rem 0 1.25rem;
        }
        .menu-brand-logo-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .menu-brand-logo {
          display: block;
          height: 7rem;
          width: 16rem;
          max-height: 7rem;
          max-width: min(16rem, 75vw);
          object-fit: contain;
          object-position: center;
        }
        @media (min-width: 640px) {
          .menu-brand-logo {
            height: 8rem;
            width: 18rem;
            max-height: 8rem;
            max-width: 18rem;
          }
        }
        .menu-brand-fallback {
          font-family: var(--vibe-font-heading);
          font-weight: 700;
          font-size: 1.75rem;
          color: #fff;
        }

        /* CTA stack — mobile: above nav; tablet (md+): below nav.
           Matches Footer › cta_tour_link (.footer-cta-button): white pill,
           primary label, surface fill + contrast ink on hover. */
        .menu-ctas {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 0.75rem;
          /* Mobile: CTAs sit above nav — pad the bottom toward the links. */
          padding: 0 0 1.75rem;
        }
        @media (min-width: 768px) {
          .menu-ctas {
            /* Tablet: CTAs sit under nav — slight top pad after the links. */
            padding: 0.25rem 0 1.75rem;
          }
        }
        .mobile-menu-panel a.menu-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-width: 0;
          padding: 1.1rem 2.5rem;
          border-radius: 999px;
          background: #fff;
          border: 1px solid #fff;
          color: var(--vibe-color-primary, #12244a);
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: 1rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          line-height: 1.25;
          text-align: center;
          text-decoration: none;
          white-space: nowrap;
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
        }
        .mobile-menu-panel a.menu-cta:hover,
        .mobile-menu-panel a.menu-cta:focus-visible {
          background: var(--vibe-color-surface, #fff);
          border-color: var(--vibe-color-surface, #fff);
          color: var(--site-cta-ink-on-surface, #fff);
          transform: translateY(-1px);
        }
        .mobile-menu-panel a.menu-cta:focus-visible {
          outline: 2px solid #ffffff;
          outline-offset: 3px;
        }

        /* Nav links */
        .menu-nav {
          display: flex;
          flex-direction: column;
          padding-bottom: 1.5rem;
        }
        /* A "group" wraps a parent nav row + its collapsible children. Parents
           without children still render as a group (single row) so the layout
           stays uniform. */
        .menu-nav-group {
          display: flex;
          flex-direction: column;
        }
        /* Row = the parent link on the left + optional chevron toggle on the
           right. Uses flex so the toggle sits at the row's right edge without
           consuming click area from the link itself. */
        .menu-nav-row {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        .menu-nav-row--has-children .menu-nav-link {
          flex: 1 1 auto;
          min-width: 0;
        }
        /* Chevron toggle. Rotates 180° when the group is open, matching the
           desktop dropdown's caret-flip. */
        .menu-nav-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.25rem;
          height: 2.25rem;
          padding: 0;
          margin: 0;
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.85);
          cursor: pointer;
          border-radius: 9999px;
          transition: color 0.2s ease, background 0.2s ease;
        }
        .menu-nav-toggle:hover {
          color: #fff;
          background: rgba(255, 255, 255, 0.08);
        }
        .menu-nav-toggle-icon {
          width: 1rem;
          height: 1rem;
          transition: transform 0.25s ease;
        }
        .menu-nav-toggle--open .menu-nav-toggle-icon {
          transform: rotate(180deg);
        }
        /* Collapsible child container. Uses max-height animation so the
           children slide open/closed. 32rem is generous — no parent group
           has more than a few children, and the value only caps the animation
           end-state, not the actual rendered height. Indented via padding
           only — no tree-branch border, to match the clean reference. */
        .menu-nav-children {
          display: flex;
          flex-direction: column;
          overflow: hidden;
          max-height: 0;
          padding-left: 1rem;
          transition: max-height 0.25s ease, padding-top 0.25s ease, padding-bottom 0.25s ease;
        }
        .menu-nav-children--open {
          max-height: 32rem;
          padding-top: 0.15rem;
          padding-bottom: 0.35rem;
        }
        /* Child link tweaks — slightly smaller, subtler weight than parents. */
        .menu-nav-link--child {
          font-size: 1rem;
          font-weight: 400;
        }
        /* Pointer-device hover: on tablets with a mouse/trackpad, hovering the
           parent group opens the submenu and closes it when the pointer leaves.
           Driven purely via CSS (not React state) so leaving the group
           implicitly collapses it — otherwise hover-open would stick after
           the pointer moved away because there's no hover-off event tied to
           state. Touch devices don't fire :hover; they use the chevron tap
           driven by JS state, which sets .menu-nav-children--open above. */
        @media (hover: hover) and (pointer: fine) {
          .menu-nav-group--has-children:hover .menu-nav-toggle-icon,
          .menu-nav-group--has-children:focus-within .menu-nav-toggle-icon {
            transform: rotate(180deg);
          }
          .menu-nav-group--has-children:hover .menu-nav-children,
          .menu-nav-group--has-children:focus-within .menu-nav-children {
            max-height: 32rem;
            padding-top: 0.15rem;
            padding-bottom: 0.35rem;
          }
        }
        .menu-nav-link {
          display: block;
          padding: 0.55rem 0;
          font-family: var(--vibe-font-body);
          font-size: 1.125rem;
          font-weight: 500;
          letter-spacing: 0.01em;
          color: rgba(255, 255, 255, 0.95);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .menu-nav-link:hover {
          color: #fff;
        }
        .menu-nav-link--active {
          color: #fff;
          font-weight: 600;
        }
        /* Hover-underline: swipes in from the LEFT edge of the label so it
           reads as a "side" reveal, not a fade-in. Anchored to the inner
           inline-flex span so the line hugs the text width instead of
           stretching across the full-width block anchor. All drawer nav
           anchors wrap their label in '.menu-nav-link > span' so this
           selector matches every item consistently. */
        .menu-nav-link > span {
          position: relative;
        }
        .menu-nav-link > span::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -0.3rem;
          width: 0;
          height: 2px;
          border-radius: 2px;
          background: currentColor;
          transition: width 0.3s ease;
          pointer-events: none;
        }
        .menu-nav-link:hover > span::after,
        .menu-nav-link:focus-visible > span::after {
          width: 100%;
        }
        /* Currently-selected page keeps the underline persistently drawn so
           the active state stays visible without needing hover. */
        .menu-nav-link--active > span::after {
          width: 100%;
        }

        /* Info blocks (Visit / Contact / Hours) */
        .menu-info {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.18);
        }
        .menu-info-block {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }
        .menu-info-label {
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #fff;
        }
        .menu-info-value {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.5;
          color: rgba(255, 255, 255, 0.95);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .menu-info-value:hover {
          color: #fff;
        }
        .menu-info-address {
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
        }
        .menu-info-address-line {
          display: block;
        }
        .menu-hours {
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        .menu-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: rgba(255, 255, 255, 0.95);
        }
        .menu-hours-row dt {
          margin: 0;
          font-weight: 500;
        }
        .menu-hours-row dd {
          margin: 0;
          font-weight: 400;
        }

        /* Global overrides for anchors within the panel.
           Exclude .menu-cta so the filled drawer pills keep primary label
           color (and their own hover: surface fill + contrast ink). */
        .mobile-menu-panel a:not(.menu-cta) {
          color: rgba(255, 255, 255, 0.95);
        }
        .mobile-menu-panel a:not(.menu-cta):hover {
          color: #fff;
        }
        .vantage-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .vantage-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {},
  slotGroups: {
    'header.logo': logoSlotDef,
    'header.schedule_tour_link': scheduleTourLinkDef,
    'header.address_link': addressLinkDef,
    'header.phone_link': phoneLinkDef,
    'header.resident_portal_link': residentPortalLinkDef,
    'header.apply_now_link': applyNowLinkDef,
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
